import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import type {
  DocumentData,
  DocumentReference,
  Query,
} from "firebase/firestore";

import { db } from "../firebase/client";
import type {
  EventModel,
  GameModel,
  InviteModel,
  PlayerModel,
  PlayoffSeriesModel,
  PregameModel,
  RemoteState,
  ScheduleModel,
  SeasonModel,
  TeamModel,
} from "../shared/types/contracts";
import {
  decodeEvent,
  decodeGame,
  decodeInvite,
  decodePlayer,
  decodePregame,
  decodePlayoffSeries,
  decodeSchedule,
  decodeSeason,
  decodeTeam,
} from "./decoders";
import {
  remoteAbsent,
  remoteFailure,
  remoteLoading,
  remoteSuccess,
} from "./remoteState";

type Decoder<T> = (value: unknown) => T;

function identified(id: string, value: unknown): Record<string, unknown> {
  const data =
    typeof value === "object" && value !== null && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  return { id, ...data };
}

function useRemoteDoc<T>(
  reference: DocumentReference<DocumentData> | null,
  decoder: Decoder<T>,
): RemoteState<T> {
  const [state, setState] = useState<RemoteState<T>>(remoteLoading);
  useEffect(() => {
    if (!reference) {
      setState(remoteAbsent());
      return;
    }
    setState(remoteLoading());
    return onSnapshot(
      reference,
      (snapshot) => {
        if (!snapshot.exists()) {
          setState(remoteAbsent());
          return;
        }
        try {
          setState(
            remoteSuccess(decoder(identified(snapshot.id, snapshot.data()))),
          );
        } catch (error) {
          setState(remoteFailure(error));
        }
      },
      (error) => setState(remoteFailure(error)),
    );
  }, [decoder, reference]);
  return state;
}

function useRemoteQuery<T>(
  source: Query<DocumentData> | null,
  decoder: Decoder<T>,
): RemoteState<T[]> {
  const [state, setState] = useState<RemoteState<T[]>>(remoteLoading);
  useEffect(() => {
    if (!source) {
      setState(remoteSuccess([]));
      return;
    }
    setState(remoteLoading());
    return onSnapshot(
      source,
      (snapshot) => {
        try {
          setState(
            remoteSuccess(
              snapshot.docs.map((entry) =>
                decoder(identified(entry.id, entry.data())),
              ),
            ),
          );
        } catch (error) {
          setState(remoteFailure(error));
        }
      },
      (error) => setState(remoteFailure(error)),
    );
  }, [decoder, source]);
  return state;
}

export function useTeamDoc(
  teamId: string | null | undefined,
): RemoteState<TeamModel> {
  const reference = useMemo(
    () => (teamId ? doc(db, "teams", teamId) : null),
    [teamId],
  );
  return useRemoteDoc(reference, decodeTeam);
}

export function usePregameDoc(
  teamId: string | null | undefined,
): RemoteState<PregameModel> {
  const reference = useMemo(
    () => (teamId ? doc(db, "teams", teamId, "pregame", "current") : null),
    [teamId],
  );
  return useRemoteDoc(reference, decodePregame);
}

export function useInviteDoc(
  teamId: string | null | undefined,
): RemoteState<InviteModel> {
  const reference = useMemo(
    () => (teamId ? doc(db, "teams", teamId, "private", "invite") : null),
    [teamId],
  );
  return useRemoteDoc(reference, decodeInvite);
}

export function useGameDoc(
  teamId: string | null | undefined,
  gameId: string | null | undefined,
): RemoteState<GameModel> {
  const reference = useMemo(
    () => (teamId && gameId ? doc(db, "teams", teamId, "games", gameId) : null),
    [gameId, teamId],
  );
  return useRemoteDoc(reference, decodeGame);
}

export function usePlayers(
  teamId: string | null | undefined,
): RemoteState<PlayerModel[]> {
  const source = useMemo(
    () => (teamId ? query(collection(db, "teams", teamId, "players")) : null),
    [teamId],
  );
  return useRemoteQuery(source, decodePlayer);
}

export function useGameEvents(
  teamId: string | null | undefined,
  gameId: string | null | undefined,
): RemoteState<EventModel[]> {
  const source = useMemo(
    () =>
      teamId && gameId
        ? query(
            collection(db, "teams", teamId, "events"),
            where("gameId", "==", gameId),
          )
        : null,
    [gameId, teamId],
  );
  return useRemoteQuery(source, decodeEvent);
}

export function useArchivedGames(
  teamId: string | null | undefined,
): RemoteState<GameModel[]> {
  const source = useMemo(
    () =>
      teamId
        ? query(
            collection(db, "teams", teamId, "games"),
            where("status", "==", "archived"),
          )
        : null,
    [teamId],
  );
  return useRemoteQuery(source, decodeGame);
}

export function useScheduleGames(
  teamId: string | null | undefined,
): RemoteState<ScheduleModel[]> {
  const source = useMemo(
    () => (teamId ? query(collection(db, "teams", teamId, "schedule")) : null),
    [teamId],
  );
  return useRemoteQuery(source, decodeSchedule);
}

export function usePlayoffSeries(
  teamId: string | null | undefined,
): RemoteState<PlayoffSeriesModel[]> {
  const source = useMemo(
    () =>
      teamId ? query(collection(db, "teams", teamId, "playoffSeries")) : null,
    [teamId],
  );
  return useRemoteQuery(source, decodePlayoffSeries);
}

export function useSeasons(
  teamId: string | null | undefined,
): RemoteState<SeasonModel[]> {
  const source = useMemo(
    () => (teamId ? query(collection(db, "teams", teamId, "seasons")) : null),
    [teamId],
  );
  return useRemoteQuery(source, decodeSeason);
}

export function useRecentEvents(
  teamId: string | null | undefined,
  max = 40,
): RemoteState<EventModel[]> {
  const source = useMemo(
    () =>
      teamId
        ? query(
            collection(db, "teams", teamId, "events"),
            orderBy("occurredAt", "desc"),
            limit(max),
          )
        : null,
    [max, teamId],
  );
  return useRemoteQuery(source, decodeEvent);
}

export function observeActionEvent(
  teamId: string,
  actionId: string,
  onCommitted: (committedRevision: number | null) => void,
): () => void {
  return onSnapshot(
    doc(db, "teams", teamId, "events", actionId),
    (snapshot) => {
      if (snapshot.exists()) {
        const revision = snapshot.data().committedRevision;
        onCommitted(Number.isInteger(revision) ? revision : null);
      }
    },
    () => {},
  );
}
